import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '@/features/admin/api/adminApi';
import { Button } from '@/shared/components/Button';
import { Spinner } from '@/shared/components/Spinner';
import { ApiError } from '@/shared/api/httpClient';
import type { EventDto } from '@/shared/types/domain';

export function EventsListPage() {
  const navigate = useNavigate();
  const [events, setEvents] = useState<EventDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  function refetch() {
    setIsLoading(true);
    adminApi.events.list().then(setEvents).finally(() => setIsLoading(false));
  }

  useEffect(refetch, []);

  return (
    <div>
      <div className="admin-page__header">
        <p style={{ color: 'var(--text-muted)', margin: 0 }}>{events.length} event{events.length === 1 ? '' : 's'}</p>
        <Button onClick={() => setShowCreate((v) => !v)}>{showCreate ? 'Cancel' : '+ New event'}</Button>
      </div>

      {showCreate && (
        <CreateEventForm
          onCreated={() => {
            setShowCreate(false);
            refetch();
          }}
        />
      )}

      {isLoading ? (
        <Spinner label="Loading events" />
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Venue</th>
              <th>Dates</th>
              <th>Status</th>
              <th>Exhibitors</th>
              <th>Delegates</th>
            </tr>
          </thead>
          <tbody>
            {events.map((event) => (
              <tr key={event.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/admin/events/${event.id}`)}>
                <td>{event.name}</td>
                <td>{event.venue}</td>
                <td>
                  {new Date(event.startDateUtc).toLocaleDateString()} – {new Date(event.endDateUtc).toLocaleDateString()}
                </td>
                <td>{event.status}</td>
                <td>{event.exhibitorCount}</td>
                <td>{event.delegateCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function CreateEventForm({ onCreated }: { onCreated: () => void }) {
  const [name, setName] = useState('');
  const [venue, setVenue] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setIsSaving(true);

    try {
      await adminApi.events.create({
        name,
        venue,
        startDateUtc: new Date(startDate).toISOString(),
        endDateUtc: new Date(endDate).toISOString(),
        duplicateScanWindowSeconds: 300
      });
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.userMessage : 'Could not create event.');
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 'var(--space-4)',
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-5)',
        marginBottom: 'var(--space-5)'
      }}
    >
      {error && (
        <div className="error-banner" style={{ gridColumn: '1 / -1' }}>
          {error}
        </div>
      )}
      <div className="field">
        <label className="field__label">Name</label>
        <input className="field__input" required value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="field">
        <label className="field__label">Venue</label>
        <input className="field__input" required value={venue} onChange={(e) => setVenue(e.target.value)} />
      </div>
      <div className="field">
        <label className="field__label">Start date</label>
        <input type="date" className="field__input" required value={startDate} onChange={(e) => setStartDate(e.target.value)} />
      </div>
      <div className="field">
        <label className="field__label">End date</label>
        <input type="date" className="field__input" required value={endDate} onChange={(e) => setEndDate(e.target.value)} />
      </div>
      <div style={{ gridColumn: '1 / -1' }}>
        <Button type="submit" isLoading={isSaving}>
          Create event
        </Button>
      </div>
    </form>
  );
}
